// Flow Trigger and Settings Modal (PRD §1.9, WP-08).
// Configures flow name, type (keyword / system / sequence), trigger rules, automation actions, and data integrations.
import { useState, useEffect } from 'react';
import { useI18n } from '../../lib/i18n';
import { useAccounts } from '../../lib/accounts';
import {
  type FlowType,
  type FlowTrigger,
  type AutomationActions,
  type DataIntegrations,
} from '../../lib/flows';
import { TIMEZONES, flowFolderList, type FlowFolderDoc, SYSTEM_ACTION_KEYS } from '../../lib/bots';
import { Button } from '../ui/Button';
import { useToast } from '../ui/Toast';
import { CloseIcon } from '../icons';

export interface StartNodeConfigData {
  name: string;
  type: FlowType;
  integrationId?: string | null;
  folderId?: string | null;
  trigger?: FlowTrigger;
  automationActions?: AutomationActions;
  dataIntegrations?: DataIntegrations;
}

interface StartNodeConfigModalProps {
  open: boolean;
  initialData: StartNodeConfigData;
  onSave: (data: StartNodeConfigData) => void;
  onClose: () => void;
}

export default function StartNodeConfigModal({
  open,
  initialData,
  onSave,
  onClose,
}: StartNodeConfigModalProps) {
  const { t } = useI18n();
  const toast = useToast();
  const { numbers } = useAccounts();

  const [name, setName] = useState(initialData.name);
  const [type, setType] = useState<FlowType>(initialData.type);
  const [integrationId, setIntegrationId] = useState(initialData.integrationId || '');
  const [folderId, setFolderId] = useState(initialData.folderId || '');
  const [matchType, setMatchType] = useState<'exact' | 'contains'>(
    initialData.trigger?.matchType || 'contains'
  );
  const [keywordsText, setKeywordsText] = useState(
    (initialData.trigger?.keywords || []).join(', ')
  );
  const [systemActionKey, setSystemActionKey] = useState(
    initialData.trigger?.systemActionKey || ''
  );
  const [deliveryTime, setDeliveryTime] = useState(initialData.trigger?.sequenceConfig?.deliveryTime || '09:00');
  const [timezone, setTimezone] = useState(initialData.trigger?.sequenceConfig?.timezone || 'Asia/Muscat');
  const [reentry, setReentry] = useState<'never' | 'after_completion' | 'always'>(
    initialData.trigger?.sequenceConfig?.reentry || 'after_completion'
  );
  const [folders, setFolders] = useState<FlowFolderDoc[]>([]);

  useEffect(() => {
    if (open) {
      flowFolderList({})
        .then((res) => {
          if (res.data?.ok && Array.isArray(res.data.folders)) {
            setFolders(res.data.folders);
          }
        })
        .catch((err) => {
          toast.error((err as Error).message || t('flow.foldersLoadError') || 'Failed to load flow folders');
        });
    }
  }, [open]);

  if (!open) return null;

  const handleSave = () => {
    const keywords = keywordsText
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    const trigger: FlowTrigger = {
      keywords: type === 'keyword' ? keywords : undefined,
      matchType: type === 'keyword' ? matchType : undefined,
      systemActionKey: type === 'system' ? systemActionKey || null : undefined,
      sequenceConfig:
        type === 'sequence'
          ? {
              deliveryTime,
              timezone,
              reentry,
            }
          : null,
    };

    onSave({
      name: name.trim() || 'Untitled Flow',
      type,
      integrationId: integrationId || null,
      folderId: folderId || null,
      trigger,
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="start-node-config-title"
        className="relative w-full max-w-xl rounded-2xl border border-line bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900 overflow-hidden flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-line px-5 py-4 dark:border-slate-800">
          <div>
            <h3 id="start-node-config-title" className="text-base font-bold text-ink dark:text-white">
              {t('flow.settings.title') || 'Flow Settings & Trigger'}
            </h3>
            <p className="mt-0.5 text-xs text-ink-3 dark:text-slate-400">
              {t('flow.settings.subtitle') || 'Configure flow name, activation conditions, and assigned WhatsApp number.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close') || 'Close'}
            className="rounded-lg p-1 text-ink-3 hover:bg-surface dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Form Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4 text-xs">
          <div>
            <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
              {t('flow.name') || 'Flow Name'} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('flow.settings.namePlaceholder') || 'e.g. Lead Qualification & Onboarding'}
              required
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.settings.flowType') || 'Flow Type'}</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as FlowType)}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <option value="keyword">{t('flow.settings.typeKeyword') || 'Keyword Reply Flow'}</option>
                <option value="system">{t('flow.settings.typeSystem') || 'System Action Flow'}</option>
                <option value="sequence">{t('flow.settings.typeSequence') || 'Drip Sequence Flow'}</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold text-ink dark:text-slate-200 mb-1">
                {t('flow.settings.account') || 'WhatsApp Account / Number'}
              </label>
              <select
                value={integrationId}
                onChange={(e) => setIntegrationId(e.target.value)}
                className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <option value="">{t('flow.settings.allNumbers') || 'All Numbers (Global)'}</option>
                {numbers.map((n) => (
                  <option key={n.id} value={n.id}>
                    {n.verifiedPhoneE164 || n.label || n.id}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block font-semibold text-ink dark:text-slate-200 mb-1">{t('flow.settings.folderCategory') || 'Folder Category'}</label>
            <select
              value={folderId}
              onChange={(e) => setFolderId(e.target.value)}
              className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
            >
              <option value="">{t('flow.settings.noFolder') || 'No Folder (Root)'}</option>
              {folders.map((f) => (
                <option key={f.id} value={f.id}>
                  📁 {f.name}
                </option>
              ))}
            </select>
          </div>

          {/* TRIGGER SETTINGS ACCORDING TO TYPE */}
          {type === 'keyword' && (
            <div className="rounded-2xl border border-line bg-surface/30 p-4 space-y-3 dark:border-slate-800 dark:bg-slate-800/30">
              <h4 className="font-bold text-ink dark:text-white">{t('flow.settings.keywordRules') || 'Keyword Trigger Rules'}</h4>
              <div>
                <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">
                  {t('flow.settings.triggerKeywords') || 'Trigger Keywords (comma separated)'}
                </label>
                <input
                  type="text"
                  value={keywordsText}
                  onChange={(e) => setKeywordsText(e.target.value)}
                  placeholder={t('flow.settings.keywordsPlaceholder') || 'hello, support, order, prices'}
                  className="w-full rounded-lg border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                />
              </div>

              <div>
                <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">{t('flow.settings.matchType') || 'Match Type'}</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setMatchType('exact')}
                    className={`rounded-lg border px-3 py-1.5 font-semibold transition-all ${
                      matchType === 'exact'
                        ? 'border-brand bg-brand/10 text-brand'
                        : 'border-line bg-white text-ink-2 dark:border-slate-700 dark:bg-slate-800'
                    }`}
                  >
                    {t('flow.settings.exactMatch') || 'Exact Match'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setMatchType('contains')}
                    className={`rounded-lg border px-3 py-1.5 font-semibold transition-all ${
                      matchType === 'contains'
                        ? 'border-brand bg-brand/10 text-brand'
                        : 'border-line bg-white text-ink-2 dark:border-slate-700 dark:bg-slate-800'
                    }`}
                  >
                    {t('flow.settings.containsPhrase') || 'Contains Phrase'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {type === 'system' && (
            <div className="rounded-2xl border border-line bg-surface/30 p-4 space-y-3 dark:border-slate-800 dark:bg-slate-800/30">
              <h4 className="font-bold text-ink dark:text-white">{t('flow.settings.systemActionTrigger') || 'System Action Trigger'}</h4>
              <div>
                <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">
                  {t('flow.settings.targetSystemAction') || 'Target System Action'}
                </label>
                <select
                  value={systemActionKey}
                  onChange={(e) => setSystemActionKey(e.target.value)}
                  className="w-full rounded-xl border border-line bg-white px-3 py-2 text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="">{t('flow.settings.selectSystemAction') || '-- Select System Action --'}</option>
                  {SYSTEM_ACTION_KEYS.map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {type === 'sequence' && (
            <div className="rounded-2xl border border-line bg-surface/30 p-4 space-y-3 dark:border-slate-800 dark:bg-slate-800/30">
              <h4 className="font-bold text-ink dark:text-white">{t('flow.settings.dripSchedule') || 'Drip Schedule & Delivery Window'}</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">
                    {t('flow.settings.preferredTime') || 'Preferred Delivery Time'}
                  </label>
                  <input
                    type="time"
                    value={deliveryTime}
                    onChange={(e) => setDeliveryTime(e.target.value)}
                    className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  />
                </div>
                <div>
                  <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">{t('flow.settings.timezone') || 'Timezone'}</label>
                  <select
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full rounded-lg border border-line bg-white px-2 py-1.5 text-xs text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                  >
                    {TIMEZONES.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block font-medium text-ink-2 dark:text-slate-300 mb-1">
                  {t('flow.settings.reentryRules') || 'Re-entry Rules'}
                </label>
                <select
                  value={reentry}
                  onChange={(e) => setReentry(e.target.value as any)}
                  className="w-full rounded-lg border border-line bg-white px-3 py-1.5 text-ink outline-none dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
                >
                  <option value="never">{t('flow.settings.reentryNever') || 'Never re-enroll contact'}</option>
                  <option value="after_completion">{t('flow.settings.reentryAfter') || 'Allow re-entry only after completion'}</option>
                  <option value="always">{t('flow.settings.reentryAlways') || 'Always re-enroll contact'}</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2.5 border-t border-line px-5 py-3 dark:border-slate-800">
          <Button variant="secondary" size="sm" onClick={onClose}>
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button size="sm" onClick={handleSave}>
            {t('flow.settings.save') || 'Save Flow Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
}
